import React, {useEffect, useState, useRef, useCallback, useMemo} from "react"
import {DateObject} from "react-multi-date-picker";
import useBooking from "../../hooks/useBooking";
import useAvailable from "../../hooks/useAvailable";
import Calendar from "../Calendar/Calendar"
//import './style.scss'

const CalendarBooking = (props) => {

  const {
    dates,
    setDates,
    booking,
    isLoadingBookings,
    loadMonthBookings,
  } = props

  // Use state instead of refs so UI updates when conflicts change
  const [conflictingDates, setConflictingDates] = useState([]);
  const [maybeConflictingDates, setMaybeConflictingDates] = useState([]);

  // Keep refs for tracking during render cycle (to avoid stale closures)
  const conflictingDatesRef = useRef([]);
  const maybeConflictingDatesRef = useRef([]);

  // Store event listeners for proper cleanup
  const eventListenersRef = useRef(new Map());

  const {isBookedBooking, isAvailableBooking, isDateInBooking, getBookingDates} = useBooking()
  const {isAvailable} = useAvailable()

  /**
   * Find the first relevant date to show in the calendar:
   * 1. If there are already selected dates, use the first one
   * 2. Otherwise, find the first date where both parties are available
   */
  const current = useMemo(() => {
    if (!booking) return null

    // Priority 1: If there are already selected dates in the booking, go to the first one
    const bookingDates = getBookingDates(booking)
    if (bookingDates && bookingDates.length > 0) {
      const sortedDates = [...bookingDates].sort((a, b) => a.toDays() - b.toDays())
      return sortedDates[0]
    }

    // Priority 2: Find the first available date where both parties are available
    const stand = booking.stand
    const spotGroup = booking.spot_group

    if (!stand && !spotGroup) return null

    // Search the next 90 days for an available date
    for (let i = 0; i < 90; i++) {
      const checkDate = new DateObject().add(i, 'days')

      let standAvailable = true
      let spotGroupAvailable = true

      // Check stand availability
      if (stand) {
        if (!isAvailable(stand, checkDate) || isBookedBooking(stand, booking, checkDate)) {
          standAvailable = false
        }
      }

      // Check spot_group availability
      if (spotGroup) {
        if (!isAvailable(spotGroup, checkDate) || isBookedBooking(spotGroup, booking, checkDate)) {
          spotGroupAvailable = false
        }
      }

      // If both are available, this is our target date
      if (standAvailable && spotGroupAvailable) {
        return checkDate
      }
    }

    return null
  }, [booking, getBookingDates, isAvailable, isBookedBooking])

  const unavailableButSelectedClassname = 'unavailable-but-selected'
  const availableAndSelectedClassname = 'available-and-selected'
  const maybeUnavailableAndSelectedClassname = 'maybe-unavailable-and-selected'

  /**
   * Cleanup event listeners on unmount
   */
  useEffect(() => {
    return () => {
      // Remove all registered event listeners
      eventListenersRef.current.forEach((handler, element) => {
        element.removeEventListener('click', handler);
      });
      eventListenersRef.current.clear();
    };
  }, []);

  /**
   * Sync refs to state after processing
   */
  const syncConflictsToState = useCallback(() => {
    setConflictingDates([...conflictingDatesRef.current]);
    setMaybeConflictingDates([...maybeConflictingDatesRef.current]);
  }, []);

  /**
   * Reset and recalculate conflicts when booking changes
   */
  useEffect(() => {
    const executeWithDelay = async () => {
      await delay(20); // Delay to ensure DOM elements are rendered
      resetDatePanelAndConflicts();
    };

    executeWithDelay();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [booking, booking?.date_ranges, booking?.dates]);

  /**
   * @param ms
   * @returns {Promise<unknown>}
   */
  const delay = (ms) => new Promise(resolve => setTimeout(resolve, ms));

  /**
   * Reset conflicts and recalculate for all booking dates
   */
  const resetDatePanelAndConflicts = () => {
    // Reset the refs
    conflictingDatesRef.current = [];
    maybeConflictingDatesRef.current = [];

    // Get dates without mutating the booking prop
    const bookingDates = getBookingDates(booking);

    bookingDates.forEach((date) => {
      setDatePanelAndConflicts(getStyleAndClassNames({style: {}, className: '', date: date}));
    });

    // Sync to state after processing all dates
    syncConflictsToState();
  }

  /**
   * @param date
   * @param today
   * @returns {{disabled: boolean, className: string, style: {}}}
   */
  const mapDays = ({date, today}) => {
    const daysFromNow = date.toDays() - today.toDays();
    const isPast = daysFromNow < 0;

    let className = '';
    let style = {};

    if (isPast) {
      style = {color: "#ccc"};
    } else {
      const conflicts = setDatePanelAndConflicts(getStyleAndClassNames({style, className, date}));
      className = conflicts.className;
      style = conflicts.style;
    }
    return {disabled: isPast, className, style};
  };

  /**
   * Determine style and className based on availability
   * @param date
   * @param className
   * @param style
   * @returns {{date, className: string, style: object}}
   */
  const getStyleAndClassNames = ({date, className, style}) => {

    const inBooking = isDateInBooking(booking, date);

    let isStandAvailable = true;
    let isSpotGroupAvailable = true;
    let isOwnerAssetAvailable = true;

    // Check for Stand availability
    if (booking.stand) {
      if (booking.user_owns_object_type === 'stand') {
        // For Stand owners, check if stand is already booked
        if (isBookedBooking(booking.stand, booking, date)) {
          isStandAvailable = false;
        }
        // Check if owner has set their stand as available (only check the stand itself, not spots)
        if (!isAvailable(booking.stand, date)) {
          isOwnerAssetAvailable = false;
        }
      } else {
        // For non-owners, check both availability and booking status
        if (
          !isAvailableBooking(booking.stand, booking, date) ||
          isBookedBooking(booking.stand, booking, date)
        ) {
          isStandAvailable = false;
        }
      }
    }

    // Check for SpotGroup availability
    if (booking.spot_group) {
      if (booking.user_owns_object_type === 'spot_group') {
        // For SpotGroup owners, check if spot_group is already booked
        if (isBookedBooking(booking.spot_group, booking, date)) {
          isSpotGroupAvailable = false;
        }
        // Check if owner has set their spot_group as available (only check spot_group itself, not individual spots)
        if (!isAvailable(booking.spot_group, date)) {
          isOwnerAssetAvailable = false;
        }
      } else {
        // For non-owners, check both availability and booking status
        if (
          !isAvailableBooking(booking.spot_group, booking, date) ||
          isBookedBooking(booking.spot_group, booking, date)
        ) {
          isSpotGroupAvailable = false;
        }
      }
    }

    // Determine final className and style based on availability
    // Priority: booking conflicts (red) > owner not available (orange) > all available (green)
    if (!isSpotGroupAvailable || !isStandAvailable) {
      // Booking conflict or other party not available (red/grey)
      className = inBooking ? unavailableButSelectedClassname : '';
      style = {color: "#ccc"};
    } else if (!isOwnerAssetAvailable) {
      // Owner's asset is not set as available, but no booking conflicts (orange)
      // This is informational: owner could still book if they wanted to
      className = inBooking ? maybeUnavailableAndSelectedClassname : '';
      style = {color: "orange"};
    } else {
      // Both are available and no conflicts (green)
      className = inBooking ? availableAndSelectedClassname : '';
      style = {color: "green"};
    }

    return {
      date: date,
      className: className,
      style: style
    };
  }

  /**
   * Update DatePanel DOM and track conflicts
   * @param date
   * @param className
   * @param style
   * @returns {{date, className, style}}
   */
  const setDatePanelAndConflicts = ({date, className, style}) => {

    const formattedDate = date.format("YYYY/MM/DD");

    // Track conflicts
    if (className === maybeUnavailableAndSelectedClassname) {
      if (!maybeConflictingDatesRef.current.includes(formattedDate)) {
        maybeConflictingDatesRef.current.push(formattedDate);
      }
      // Remove from conflicting if it was there
      const conflictIndex = conflictingDatesRef.current.indexOf(formattedDate);
      if (conflictIndex !== -1) {
        conflictingDatesRef.current.splice(conflictIndex, 1);
      }
    } else if (className === unavailableButSelectedClassname) {
      if (!conflictingDatesRef.current.includes(formattedDate)) {
        conflictingDatesRef.current.push(formattedDate);
      }
      // Remove from maybe-conflicting if it was there
      const maybeIndex = maybeConflictingDatesRef.current.indexOf(formattedDate);
      if (maybeIndex !== -1) {
        maybeConflictingDatesRef.current.splice(maybeIndex, 1);
      }
    } else {
      // Available - remove from both lists
      const conflictIndex = conflictingDatesRef.current.indexOf(formattedDate);
      if (conflictIndex !== -1) {
        conflictingDatesRef.current.splice(conflictIndex, 1);
      }
      const maybeIndex = maybeConflictingDatesRef.current.indexOf(formattedDate);
      if (maybeIndex !== -1) {
        maybeConflictingDatesRef.current.splice(maybeIndex, 1);
      }
    }

    // Direct DOM manipulation to update DatePanel styling
    const panelBody = document.querySelector('.rmdp-panel-body');
    if (panelBody) {
      const dateElements = panelBody.querySelectorAll('li');
      dateElements.forEach(li => {
        const span = li.querySelector('span');
        if (span && span.textContent.includes(formattedDate)) {
          // Reset only our custom classes, preserve library classes
          li.classList.remove(
            unavailableButSelectedClassname,
            availableAndSelectedClassname,
            maybeUnavailableAndSelectedClassname
          );

          // Add the new class if present
          if (className) {
            li.classList.add(className);
          }

          // Add click listener if not already added
          if (!eventListenersRef.current.has(span)) {
            const handler = () => loadMonthBookings(date);
            span.addEventListener('click', handler);
            eventListenersRef.current.set(span, handler);
          }

          Object.assign(li.style, style);
        }
      });
    }

    return {
      date: date,
      className: className,
      style: style,
    };
  }

  const isStandOwner = booking.user_owns_object_type === 'stand';
  const ownerAssetLabel = isStandOwner ? 'jouw stand' : 'jouw locatie';

  return (
    <div className="component-calendar-booking">
      <Calendar
        dates={dates}
        setDates={setDates}
        isLoadingBookings={isLoadingBookings}
        loadMonthBookings={loadMonthBookings}
        mapDays={mapDays}
        current={current}
      />
      {conflictingDates.length > 0 && (
        <div className="conflicting-dates">
          {conflictingDates.length} conflicterende {conflictingDates.length === 1 ? 'datum' : 'datums'} gevonden.
        </div>
      )}
      {maybeConflictingDates.length > 0 && (
        <div className="maybe-conflicting-dates">
          {maybeConflictingDates.length} {maybeConflictingDates.length === 1 ? 'datum' : 'datums'} waarop {ownerAssetLabel} niet beschikbaar is voor anderen.
        </div>
      )}
      <div className="legend">
        <div className="row">
          <div className="dot green"></div>
          <div className="label">Beschikbaar voor beide partijen</div>
        </div>
        <div className="row">
          <div className="dot orange"></div>
          <div className="label">
            {isStandOwner
              ? 'Jouw stand is niet beschikbaar ingesteld, maar de locatie is wel beschikbaar'
              : 'Jouw locatie is niet beschikbaar ingesteld, maar de stand is wel beschikbaar'
            }
          </div>
        </div>
        <div className="row">
          <div className="dot red"></div>
          <div className="label">De stand en/of de locatie hebben al een boeking op deze dag</div>
        </div>
        <div className="row">
          <div className="dot grey"></div>
          <div className="label">
            {isStandOwner
              ? 'De locatie is niet beschikbaar op deze dag'
              : 'De stand is niet beschikbaar op deze dag'
            }
          </div>
        </div>
      </div>
    </div>
  );
}

CalendarBooking.propTypes = {
  // enableDisabledDays: PropTypes.bool,
  // showHeader: PropTypes.bool,
  // dates: PropTypes.object.isRequired,
  // setDates: PropTypes.object.isRequired,
  // bookable: PropTypes.object.isRequired,
  // booking: PropTypes.object.isRequired,
}

export default CalendarBooking
